import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, MapPin, Heart, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { updateProfile, getInterests, getCampuses } from '../lib/api';
import { toast } from 'sonner';

const INTEREST_ICONS = {
  Party: '🎉',
  Study: '📚',
  Sports: '⚽',
  Food: '🍕',
  Art: '🎨',
  Adventure: '🏔️',
};

export default function SetupPage() {
  const [step, setStep] = useState(1);
  const [initials, setInitials] = useState('');
  const [campus, setCampus] = useState('Edinburgh');
  const [interests, setInterests] = useState([]);
  const [availableInterests, setAvailableInterests] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [intRes, campRes] = await Promise.all([getInterests(), getCampuses()]);
      setAvailableInterests(intRes.data);
      setCampuses(campRes.data);
      if (user?.email) {
        setInitials(user.email.slice(0, 2).toUpperCase());
      }
    } catch (error) {
      console.error('Failed to load data', error);
    }
  }, [user?.email]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleInterest = (interest) => {
    setInterests((prev) =>
      prev.includes(interest) ? prev.filter((i) => i !== interest) : [...prev, interest]
    );
  };

  const handleSubmit = async () => {
    if (!initials || initials.length < 1 || initials.length > 3) {
      toast.error('Initials must be 1-3 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await updateProfile({ initials: initials.toUpperCase(), campus, interests });
      updateUser(res.data);
      toast.success('Profile setup complete!');
      navigate('/feed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-transparent">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all ${
                s <= step ? 'w-12 bg-primary' : 'w-8 bg-muted'
              }`}
            />
          ))}
        </div>

        {step === 1 && (
          <Card className="border-border/50">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <User className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="text-2xl">Your Identity</CardTitle>
              <CardDescription>Choose your display initials (1-3 characters)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="initials">Display Initials</Label>
                <Input
                  id="initials"
                  data-testid="initials-input"
                  value={initials}
                  onChange={(e) => setInitials(e.target.value.slice(0, 3).toUpperCase())}
                  placeholder="AB"
                  className="h-14 text-center text-2xl font-bold uppercase tracking-widest"
                  maxLength={3}
                />
                <p className="text-xs text-muted-foreground text-center">
                  This is how you&apos;ll appear in chats (privacy first!)
                </p>
              </div>

              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-primary flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary-foreground">
                    {initials || '??'}
                  </span>
                </div>
              </div>

              <Button
                data-testid="step1-next-btn"
                className="w-full h-12"
                onClick={() => setStep(2)}
                disabled={!initials || initials.length < 1}
              >
                Continue
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        )}

        {step === 2 && (
          <Card className="border-border/50">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="text-2xl">Your Campus</CardTitle>
              <CardDescription>Select your Heriot-Watt campus</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Select value={campus} onValueChange={setCampus}>
                <SelectTrigger className="h-14" data-testid="campus-select">
                  <SelectValue placeholder="Select campus" />
                </SelectTrigger>
                <SelectContent>
                  {campuses.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 h-12" onClick={() => setStep(1)}>
                  Back
                </Button>
                <Button
                  data-testid="step2-next-btn"
                  className="flex-1 h-12"
                  onClick={() => setStep(3)}
                >
                  Continue
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {step === 3 && (
          <Card className="border-border/50">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="text-2xl">Your Interests</CardTitle>
              <CardDescription>Select what you're into (pick at least one)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-3">
                {availableInterests.map((interest) => (
                  <button
                    key={interest}
                    data-testid={`interest-${interest.toLowerCase()}`}
                    onClick={() => toggleInterest(interest)}
                    className={`p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                      interests.includes(interest)
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <span className="text-2xl">{INTEREST_ICONS[interest]}</span>
                    <span className="font-medium">{interest}</span>
                    {interests.includes(interest) && (
                      <Check className="ml-auto w-4 h-4 text-primary" />
                    )}
                  </button>
                ))}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" className="flex-1 h-12" onClick={() => setStep(2)}>
                  Back
                </Button>
                <Button
                  data-testid="complete-setup-btn"
                  className="flex-1 h-12"
                  onClick={handleSubmit}
                  disabled={interests.length === 0 || loading}
                >
                  {loading ? 'Saving...' : 'Complete Setup'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  );
}
